import React, { useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { SendHorizonal, Loader2, Bot, Sparkles } from 'lucide-react';
import { ScrollAreaWithChildren } from '@/components/ui/scroll-area-with-children';
import { ChatMessage } from '@/store';
import { LoadingIcon } from '@/components/ui/loading-icon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Badge } from "@/components/ui/badge";

interface ChatInterfaceProps {
  messages: ChatMessage[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  isLoading: boolean;
  handleSendMessage: (e: React.FormEvent) => void;
}

export const ChatInterface = ({
  messages,
  inputMessage,
  setInputMessage,
  isLoading,
  handleSendMessage,
}: ChatInterfaceProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      
      // Add padding to the bottom of the scroll area
      const scrollContainer = messagesEndRef.current.closest('.h-full');
      if (scrollContainer) {
        (scrollContainer as HTMLElement).style.paddingBottom = '24px';
      }
    }
  }, [messages]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
  }, [inputMessage]);

  const formatTimestamp = (timestamp: Date | undefined) => {
    if (!timestamp) {
      return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
    return timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  // Check if the message contains financial attribution metadata
  const hasFinancialInfo = (message: ChatMessage) => {
    return message.metadata && 
      (message.metadata.model_used || message.metadata.rag_sources_used);
  };

  return (
    <div className="flex flex-col h-full">
      {messages.length > 0 && (
        <div className="flex-1 overflow-auto">
          <div className="min-h-[500px]">
            <ScrollAreaWithChildren className="h-full">
              <div className="px-4 py-4">
                {messages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`mb-6 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
                  >
                    <div
                      className={`inline-block max-w-[75%] ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg rounded-2xl px-5 py-3'
                          : hasFinancialInfo(message)
                            ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 border border-blue-200 shadow-md rounded-2xl px-5 py-4'
                            : 'bg-white border border-gray-200 text-gray-800 shadow-md rounded-2xl px-5 py-4'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center shadow-sm
                            ${hasFinancialInfo(message) 
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white' 
                              : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'}`
                          }>
                            {hasFinancialInfo(message) 
                              ? <Sparkles className="h-4 w-4" /> 
                              : <Bot className="h-4 w-4" />
                            }
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-700">
                              {hasFinancialInfo(message) 
                                ? 'Financial Analysis Engine' 
                                : 'Stewie AI Assistant'
                              }
                            </span>
                            
                            {hasFinancialInfo(message) && (
                              <Badge variant="outline" className="text-xs bg-white/50 border-blue-200 text-blue-700">
                                Financial AI
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="text-sm break-words whitespace-pre-wrap leading-relaxed">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            code({ node, inline, className, children, ...props }: {
                              node?: any;
                              inline?: boolean;
                              className?: string;
                              children: React.ReactNode;
                              [key: string]: any;
                            }) {
                              return inline ? (
                                <code className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-mono text-xs" {...props}>{children}</code>
                              ) : (
                                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto my-3 border border-gray-700">
                                  <code className="text-sm">{children}</code>
                                </pre>
                              );
                            },
                            a({ node, ...props }) {
                              return <a className="text-blue-600 hover:text-blue-800 underline font-medium" {...props} />;
                            }
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                      
                      {hasFinancialInfo(message) && message.metadata && (
                        <div className="mt-3 pt-3 border-t border-blue-200/50">
                          <div className="bg-blue-100/50 rounded-lg p-3">
                            {message.metadata.model_used && (
                              <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="h-3 w-3 text-blue-600" />
                                <span className="text-xs font-medium text-blue-700">
                                  Model: {message.metadata.model_used}
                                </span>
                              </div>
                            )}
                            {message.metadata.rag_sources_used && message.metadata.rag_sources_used.length > 0 && (
                              <div className="flex items-center gap-2">
                                <Sparkles className="h-3 w-3 text-blue-600" />
                                <span className="text-xs font-medium text-blue-700">
                                  Data Sources: {Array.isArray(message.metadata.rag_sources_used) 
                                    ? message.metadata.rag_sources_used.join(', ') 
                                    : message.metadata.rag_sources_used}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-2 px-2">
                      {formatTimestamp(message.timestamp)}
                    </div>
                  </div>
                ))}
                
                {/* Enhanced Loading indicator matching dashboard style */}
                {isLoading && (
                  <div className="flex justify-start mb-4">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl px-5 py-3 shadow-md">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                        <LoadingIcon size="sm" className="text-white" />
                      </div>
                      <span className="text-sm font-medium text-blue-700">Stewie is analyzing...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollAreaWithChildren>
          </div>
        </div>
      )}

      {/* Enhanced Input Area with Premium Design matching dashboard chats */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 border-t border-gray-200 mt-4 flex-shrink-0">
        <div className="p-6">
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div className="relative">
              <textarea
                ref={textareaRef}
                placeholder="Continue this conversation..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                rows={1}
                wrap="soft"
                disabled={isLoading}
                className="w-full rounded-xl border-2 border-gray-200 bg-white px-5 py-4 pr-20 resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm placeholder-gray-500 shadow-sm"
                style={{ 
                  minHeight: '60px',
                  maxHeight: '120px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              
              {/* Action Button */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Button
                  type="submit"
                  disabled={isLoading || !inputMessage.trim()}
                  className={`h-8 px-4 rounded-lg transition-all duration-200 ${
                    inputMessage.trim().length > 0 && !isLoading
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span className="text-sm">Sending...</span>
                    </>
                  ) : (
                    <>
                      <SendHorizonal className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">Send</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
